import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Cloud, Server, Database, GitBranch, Gauge,
  ArrowRight, CheckCircle, Shield, Zap
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';

const capabilities = [
  {
    icon: Cloud,
    title: 'Cloud Strategy & Architecture',
    description: 'Design cloud-native architectures optimized for Guidewire Cloud Platform with scalability and security in mind.',
    features: [
      'Architecture assessment',
      'Cloud readiness evaluation',
      'Migration roadmap',
      'Cost optimization',
    ],
  },
  {
    icon: Database,
    title: 'Data Migration & Conversion',
    description: 'Seamless data migration with comprehensive validation to ensure data integrity and business continuity.',
    features: [
      'Data mapping & profiling',
      'ETL development',
      'Validation frameworks',
      'Parallel run support',
    ],
  },
  {
    icon: GitBranch,
    title: 'CI/CD for Guidewire',
    description: 'Implement modern DevOps practices with automated pipelines for faster, more reliable deployments.',
    features: [
      'Pipeline automation',
      'Environment management',
      'Automated testing',
      'Release orchestration',
    ],
  },
  {
    icon: Gauge,
    title: 'Performance & Scalability',
    description: 'Optimize system performance with proactive monitoring, tuning, and capacity planning.',
    features: [
      'Performance testing',
      'Capacity planning',
      'Auto-scaling configuration',
      'Cost optimization',
    ],
  },
];

const roadmapSteps = [
  { 
    phase: 'Assess', 
    title: 'Assessment & Planning',
    description: 'Evaluate current state, define migration scope, and create detailed roadmap',
    icon: Shield 
  },
  { 
    phase: 'Prepare', 
    title: 'Preparation & Design',
    description: 'Architecture design, data mapping, and environment setup',
    icon: Server 
  },
  { 
    phase: 'Migrate', 
    title: 'Migration & Testing',
    description: 'Execute migration, validate data, and perform comprehensive testing',
    icon: Database 
  },
  { 
    phase: 'Optimize', 
    title: 'Optimization & Go-Live',
    description: 'Performance tuning, UAT, and production deployment',
    icon: Zap 
  },
];

const CloudModernisation = () => {
  return (
    <div data-testid="cloud-modernisation-page">
      <SEO {...pageSEO.cloudModernisation} />
      <PageHeader
        label="Cloud Services"
        title="Guidewire Cloud Migration & Legacy Modernisation"
        description="Accelerate your journey to Guidewire Cloud with our proven migration methodology and cloud-native expertise."
      />

      {/* Overview Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Cloud className="w-6 h-6 text-emerald-600" />
                </div>
                <span className="text-emerald-600 font-semibold">Cloud Migration</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
                Future-Proof Your Insurance Platform
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                The move to Guidewire Cloud Platform offers significant benefits — 
                reduced infrastructure costs, automatic upgrades, enhanced security, 
                and elastic scalability. Our migration experts ensure a smooth transition.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                Whether you're migrating from on-premise Guidewire or modernizing 
                legacy systems, we provide end-to-end support from strategy to 
                production deployment.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/contact">
                  <Button 
                    size="lg"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    Start Your Cloud Journey
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              {/* Cloud Architecture Diagram */}
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-br from-emerald-100 to-sky-100 rounded-3xl rotate-2 scale-105" />
                <div className="relative bg-white rounded-2xl shadow-xl p-8">
                  <div className="text-center mb-6">
                    <Cloud className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                    <h4 className="text-lg font-bold text-slate-900 dark:text-white">Guidewire Cloud Platform</h4>
                    <p className="text-sm text-slate-500">Enterprise-grade cloud infrastructure</p>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    {['PolicyCenter', 'ClaimCenter', 'BillingCenter'].map((app, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ delay: index * 0.1 }}
                        className="p-4 rounded-xl bg-slate-50 text-center"
                      >
                        <div className="text-sm font-semibold text-slate-700">{app}</div>
                      </motion.div>
                    ))}
                  </div>
                  <div className="mt-6 p-4 rounded-xl bg-emerald-50 border border-emerald-100">
                    <div className="flex items-center gap-2 text-emerald-700 text-sm font-medium">
                      <CheckCircle className="w-4 h-4" />
                      Auto-scaling & High Availability
                    </div>
                  </div>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Migration Roadmap */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-emerald-500/20 text-emerald-300 text-sm font-semibold rounded-full mb-4">
              Migration Roadmap
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4 tracking-tight">
              Your Path to the Cloud
            </h2>
            <p className="text-base lg:text-lg text-slate-300 max-w-2xl mx-auto">
              Our proven four-phase methodology ensures a successful migration with minimal risk.
            </p>
          </AnimatedSection>

          <div className="relative">
            {/* Connection Line */}
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-emerald-500/30 via-emerald-500 to-emerald-500/30 -translate-y-1/2" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
              {roadmapSteps.map((step, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.15 }}
                  className="relative"
                >
                  <div className="bg-slate-800/50 backdrop-blur-sm rounded-2xl p-6 border border-slate-700 h-full">
                    <div className="w-14 h-14 rounded-full bg-emerald-500 flex items-center justify-center mb-4 shadow-lg shadow-emerald-500/30">
                      <step.icon className="w-7 h-7 text-white" />
                    </div>
                    <div className="text-emerald-400 text-sm font-semibold uppercase tracking-wider mb-2">
                      Phase {index + 1}: {step.phase}
                    </div>
                    <h4 className="text-xl font-bold text-white mb-2">
                      {step.title}
                    </h4>
                    <p className="text-slate-400 text-sm leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-emerald-100 text-emerald-700 text-sm font-semibold rounded-full mb-4">
              Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Our Cloud Expertise
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive cloud services to accelerate your Guidewire modernization journey.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <StaggerItem key={index}>
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-14 h-14 rounded-xl bg-emerald-50 flex items-center justify-center mb-6">
                    <capability.icon className="w-7 h-7 text-emerald-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {capability.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {capability.description}
                  </p>
                  <ul className="space-y-2">
                    {capability.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-emerald-600 to-emerald-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Ready to Accelerate Your Cloud Journey?
            </h2>
            <p className="text-lg text-emerald-100 max-w-2xl mx-auto mb-8">
              Let our cloud experts help you plan and execute a successful 
              migration to Guidewire Cloud Platform.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-white text-emerald-700 hover:bg-emerald-50 h-14 px-8"
              >
                Schedule a Cloud Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default CloudModernisation;
